import {Component, computed, inject, signal} from '@angular/core';
import {fuseAnimations} from '../../../../core/animations';
import {MaterialModule} from '../../../../core/modules/material.module';
import {SharedModule} from '../../../../core/modules/shared.module';
import {PagesContainerComponent} from '../../pages-container/pages-container.component';
import {CompetencyService} from '../../../../core/services/competency.service';
import {applyEach, form, FormField, minLength, required} from '@angular/forms/signals';
import {Observable} from 'rxjs';
import {ToastrService} from 'ngx-toastr';
import {TranslateService} from '@ngx-translate/core';
import {Router} from '@angular/router';
import {CheckModalComponent} from '../../check-save-modal/check-modal';
import { isEqual } from 'lodash';
import {MatDialog} from '@angular/material/dialog';
import {MatSnackBar} from '@angular/material/snack-bar';
import {moveItemInArray} from '@angular/cdk/drag-drop';

interface Competency {
  title: string;
  color: string;
  skills: Skill[];
}

interface Skill {
  title: string;
  orderId: number;
}

const initialData: Competency = {
  title: '',
  color: '',
  skills: [{title: '', orderId: 0}]
};

@Component({
  selector: 'competency-detail',
  templateUrl: './competency-detail.component.html',
  styleUrls: ['./competency-detail.component.scss'],
  standalone: true,
  animations: fuseAnimations,
  imports: [MaterialModule, SharedModule, PagesContainerComponent, FormField],
  providers: [CompetencyService]
})

export class CompetencyDetailComponent {
  private competencyService = inject(CompetencyService);
  private toastr = inject(ToastrService);
  private translate = inject(TranslateService);
  private router = inject(Router);
  private dialog = inject(MatDialog);
  private snackBar = inject(MatSnackBar);

  competencyModel = signal<Competency>(initialData);

  competencyForm = form(this.competencyModel, (competency) => {
    required(competency.title, {message: 'competency.title_required'});
    required(competency.color, {message: 'competency.color_required'});
    minLength(competency.skills, 1);
    applyEach(competency.skills, (skill) => {
      required(skill.title, {message: 'competency.skill_title_required'});
    });
  });

  isChanged = computed(() => !isEqual(this.competencyModel(), initialData));

  returnBack;

  addCompetency() {
    if (this.competencyForm().valid()) {
      let data = this.competencyModel();
      this.competencyService.addCompetency(data).subscribe((res: any) => {
        if (res) {
          let title = this.translate.instant('successfully');
          let msg = this.translate.instant('space_created');
          this.toastr.success(msg, title);
          this.returnBack = true;
          this.router.navigate([ './pages/settings/competency' ]);
        }
      });
    }
  }

  onSettingsChange(color) {
    this.competencyForm.color().value.set(color.bg);
  }

  addSkill() {
    this.competencyModel.update((competency) => ({
      ...competency,
      skills: [...competency.skills, {title: '', orderId: competency.skills.length}]
    }));
  }

  removeSkill(index) {
    this.competencyModel.update((competency) => {
      let updatedSkills = competency.skills.filter((_, i) => i !== index);
      if (updatedSkills.length === 0) {
        updatedSkills = [{ title: '', orderId: 0 }];
      }
      return {
        ...competency,
        skills: updatedSkills
      };
    });
  }

  canDeactivate (): Observable<boolean> | boolean {
    if (this.returnBack) {
      return true;
    } else {
      return this.goBack();
    }
  }

  goBack(): any{
    if (this.isChanged() && !this.returnBack) {
      let dialogRef = this.dialog.open(CheckModalComponent, {
        panelClass: 'unsave-form-dialog',
        width: '400px',
      });

      return dialogRef.afterClosed().toPromise().then((result: any) => {
        if (result === 'leave') {
          return true;
        } else if (result === 'save') {
          if (this.competencyForm().valid()) {
            this.addCompetency();
            return true;
          } else {
            let text = this.translate.instant('fill_req_field');
            this.snackBar.open(text, '', {duration: 3500});
            return false;
          }
        }
      });
    } else {
      return true;
    }
  }

  drop(event: any) {
    const skills = [...this.competencyModel().skills];
    moveItemInArray(skills, event.previousIndex, event.currentIndex);
    this.competencyModel.update(prev => ({
      ...prev,
      skills: skills.map((el, i) => { el.orderId = i; return el; })
    }));
  }
}
